/** @jsxImportSource react */
'use client';

import { useEffect } from 'react';

import { usePathname } from 'next/navigation';

import { track } from './analytics';
import { useDwell, useScrollDepth } from './engagement-hooks';

// Global engagement instruments. Mounted once in root layout. Picks up
// behavior we don't want to wire on a per-component basis:
//
//   - scroll_depth_25/50/75/100 (per pathname, fires once each)
//   - outbound_clicked (mailto, tel, and any http(s) host that isn't onark.app)
//   - faq_opened (any <details> open inside the page)
//
// Everything keys off the current pathname so events on /vs/spreadsheets are
// distinguishable from events on /pricing without each page wiring its own
// tracker. Resets when pathname changes (SPA navigation).

// Scroll/dwell mechanics live in @onark/shared/engagement, shared with the v2
// signup shell's tracker (apps/frontend/app/start/_components). Only the emit
// differs — this publishes pathname-keyed marketing events, that one publishes
// catalog events keyed on the ad `landing`.

export function EngagementTracker() {
  const pathname = usePathname();

  // Time-on-page milestones — only counts active foreground time.
  useDwell(
    seconds => track('time_on_page', { path: pathname, seconds }),
    pathname
  );

  // Docs read — fire when a /docs/:slug page hits 75% scroll. Cheaper to do
  // here than per-page since every docs article uses the same chrome.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!pathname?.startsWith('/docs/')) return;
    let fired = false;
    const onScroll = () => {
      if (fired) return;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;

      if (scrollable <= 0) return;
      const pct = (window.scrollY / scrollable) * 100;

      if (pct >= 75) {
        fired = true;
        track('docs_read', { path: pathname });
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  // Scroll depth — reset per route change.
  useScrollDepth(
    milestone => track('scroll_depth', { milestone, path: pathname }),
    pathname
  );

  // Delegated click tracking — outbound links + FAQ opens.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;

      if (!target) return;

      const anchor = target.closest('a');

      if (anchor) {
        const href = anchor.getAttribute('href') ?? '';

        if (!href) return;

        if (href.startsWith('mailto:')) {
          track('outbound_clicked', {
            href,
            kind: 'mailto',
            path: pathname,
          });
        } else if (href.startsWith('tel:')) {
          track('outbound_clicked', { href, kind: 'tel', path: pathname });
        } else if (/^https?:\/\//i.test(href)) {
          try {
            const url = new URL(href);

            if (!url.hostname.endsWith('onark.app')) {
              track('outbound_clicked', {
                host: url.hostname,
                href,
                kind: 'external',
                path: pathname,
              });
            }
          } catch {
            // ignore malformed URLs
          }
        }
      }
    };

    const onToggle = (e: Event) => {
      const node = e.target as HTMLDetailsElement | null;

      if (node?.tagName !== 'DETAILS') return;
      if (!node.open) return;
      const summary = node.querySelector('summary')?.textContent?.trim() ?? '';

      track('faq_opened', { path: pathname, question: summary });
    };

    document.addEventListener('click', onClick, { capture: true });
    // <details> doesn't bubble 'toggle' by default — use capture phase.
    document.addEventListener('toggle', onToggle, { capture: true });

    return () => {
      document.removeEventListener('click', onClick, { capture: true });
      document.removeEventListener('toggle', onToggle, { capture: true });
    };
  }, [pathname]);

  return null;
}
